import ddt from '@wix/da-ddt';
import camelCase from 'lodash/camelCase';
import snakeCase from 'lodash/snakeCase';
import { PapiDacheckoutCorePackages, PapiProductV3 } from '@wix/da-papi-types';
import { type PackagePickerStoreSlice } from '../../redux/packagePicker/types';
import { CoreTypes } from './types';
import {
  formatDate,
  parseIsoDateOrTimestamp,
} from '@wix/da-i18next/pkg/time/functions';

const logger = ddt.createLogger('herotable');

export const getPackagePickerConfigOrder = (
  packageConfig?: PapiDacheckoutCorePackages['packageConfig'],
  pickerMode: PackagePickerStoreSlice['pickerMode'] = 'buy'
): CoreTypes[] => {
  packageConfig = packageConfig || {};
  return Object.keys(packageConfig)
    .filter(key =>
      getShowCoreTypeInPackagePicker(
        key as CoreTypes,
        packageConfig,
        pickerMode
      )
    )
    .map(key => ({ ...(packageConfig?.[key] || {}), key }))
    .sort((a, b) => (a?.position > b?.position ? 1 : -1))
    .map(({ key }) => snakeCase(key) as CoreTypes);
};

export const getRecommendedPackageType = (
  packageConfig?: PapiDacheckoutCorePackages['packageConfig']
): CoreTypes | undefined =>
  snakeCase(
    Object.keys(packageConfig || {})
      .filter(key => packageConfig?.[key]?.recommended === true)
      .pop()
  ) as CoreTypes;

export const getShowCoreTypeInPackagePicker = (
  coreType: CoreTypes,
  packageConfig?: PapiDacheckoutCorePackages['packageConfig'],
  pickerMode: PackagePickerStoreSlice['pickerMode'] = 'buy'
): boolean => {
  const coreTypeConfig = (packageConfig || {})?.[camelCase(coreType)];
  // There are 3 cases:
  //   1. coreType is missing from the packageConfig
  //      This means that it's disabled, and the user is not on this plan.
  //      The coreType line item should not be shown
  if (coreTypeConfig === undefined) {
    return false;
  }
  //   2. coreType is disabled in packageConfig
  //      This means that it's disabled, abut the user is on this plan.
  //      We show this coreType line item, but we disable it
  if (coreTypeConfig.enabled === false) {
    // only if the pickerMod is "buy"
    // i.e. we don't show it when the pickerMode is "gift"
    return pickerMode === 'buy';
  }
  //   3. coreType is enabled in packageConfig
  //      We show it
  return true;
};

export const formatSaleEndDate = (date: string) => {
  return `${formatDate(date, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h24',
    timeZone: 'UTC',
  } as Intl.DateTimeFormatOptions)} GMT`;
};

/** returns iso8061 end date of the current sale or undefined if it can't be determined */
export function getSaleEndDate(
  sale: PackagePickerStoreSlice['sale'],
  userJoinDate?: string
): string | undefined /* iso8061 */ {
  if (!sale) {
    return undefined;
  }

  if (!sale.daysAfterJoin) {
    return sale.saleEndDate;
  }

  try {
    if (!userJoinDate || sale.daysAfterJoin <= 0) {
      return undefined;
    }
    const relativeDate = parseIsoDateOrTimestamp(userJoinDate);
    relativeDate.setDate(relativeDate.getDate() + sale.daysAfterJoin);
    // join-relative sales always end at 8:59am GMT
    relativeDate.setUTCHours(8, 59, 0);
    return relativeDate.toISOString();
  } catch (err) {
    logger.error('Error creating sale date relative to user join', {
      sale,
      userJoinDate,
    });
    return undefined;
  }
}

export const getPriceForPackagePicker = ({
  monthlyPackage,
  yearlyPackage,
  showDiscount,
}) => {
  if (monthlyPackage.discount && showDiscount) {
    return monthlyPackage.price;
  }
  return showDiscount
    ? yearlyPackage.monthlyPrice
    : // if monthlyPriceBeforeDiscount is set but showDiscount = false,
      // that's prorated discount that is not shown as a discount in PP - we simply display a discounted value
      (yearlyPackage.monthlyPriceBeforeDiscount ?? yearlyPackage.monthlyPrice);
};

type getPriceForPackagePickerV3Params = {
  monthlyPackageProduct: PapiProductV3;
  yearlyPackageProduct: PapiProductV3;
  showDiscount: boolean;
};
export const getPriceForPackagePickerV3 = ({
  monthlyPackageProduct,
  yearlyPackageProduct,
  showDiscount,
}: getPriceForPackagePickerV3Params) => {
  if (monthlyPackageProduct.discount && showDiscount) {
    return monthlyPackageProduct.price.usd;
  }
  return showDiscount
    ? yearlyPackageProduct.corePackage?.monthlyPrice
    : // if monthlyPriceBeforeDiscount is set but showDiscount = false,
      // that's prorated discount that is not shown as a discount in PP - we simply display a discounted value
      (yearlyPackageProduct.corePackage?.monthlyPriceBeforeDiscount ??
        yearlyPackageProduct.corePackage?.monthlyPrice);
};

export const getPriceBeforeDiscountForPackagePicker = ({
  monthlyPackage,
  yearlyPackage,
}) => {
  if (monthlyPackage.discount) {
    return monthlyPackage.priceBeforeDiscount;
  }
  return yearlyPackage.monthlyPriceBeforeDiscount;
};

type getPriceBeforeDiscountForPackagePickerV3Params = {
  monthlyPackageProduct: PapiProductV3;
  yearlyPackageProduct: PapiProductV3;
};
export const getPriceBeforeDiscountForPackagePickerV3 = ({
  monthlyPackageProduct,
  yearlyPackageProduct,
}: getPriceBeforeDiscountForPackagePickerV3Params) => {
  if (monthlyPackageProduct.discount) {
    return monthlyPackageProduct.discount?.priceBeforeDiscount.usd;
  }
  return yearlyPackageProduct.corePackage?.monthlyPriceBeforeDiscount;
};
